import React, { useCallback, useEffect, useMemo, useState } from "react";
import DefaultModal from "../../modals/DefaultModal/DefaultModal";
import ModalTitle from "../../modals/ModalTitle/ModalTitle";
import {
  buildBmsSummary,
  hasAnyBmsMetric,
  normalizeBmsRawInput,
} from "../../../utils/bmsRawData";
import styles from "./styles.module.scss";

interface Props {
  bmsLastRawData: unknown;
  /** "compact" — картка в таблиці; "detail" — блок на сторінці акумулятора */
  variant?: "compact" | "detail";
}

const fmt = (n: number | undefined, fractionDigits: number): string =>
  n === undefined ? "—" : n.toFixed(fractionDigits);

const BmsBatteryStatus: React.FC<Props> = ({ bmsLastRawData, variant = "detail" }) => {
  const [rawOpen, setRawOpen] = useState(false);
  const normalized = useMemo(() => normalizeBmsRawInput(bmsLastRawData), [bmsLastRawData]);
  const summary = useMemo(() => {
    if (!normalized) {
      return null;
    }
    const built = buildBmsSummary(normalized);
    return hasAnyBmsMetric(built) ? built : null;
  }, [normalized]);

  useEffect(() => {
    if (!rawOpen) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setRawOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [rawOpen]);

  const openRaw = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setRawOpen(true);
  }, []);

  if (!normalized) {
    return <span className={styles.muted}>Немає даних BMS</span>;
  }

  if (!summary) {
    return (
      <div className={styles.wrap}>
        <span className={styles.muted}>Дані є, формат полів нестандартний</span>
        <button type="button" className={styles.rawButton} onClick={openRaw}>
          Сирі дані (JSON)
        </button>
        {rawOpen && (
          <DefaultModal>
            <div className={styles.modalInner} onClick={(e) => e.stopPropagation()}>
              <ModalTitle title="Сирі дані BMS" subTitle="Як отримано з BMS" onClose={() => setRawOpen(false)} />
              <pre className={styles.rawPre}>{JSON.stringify(normalized, null, 2)}</pre>
            </div>
          </DefaultModal>
        )}
      </div>
    );
  }

  const tempLine =
    summary.validTemperatures.length > 0
      ? summary.validTemperatures.map((t) => `${fmt(t, 1)} °C`).join(", ")
      : "—";

  const gridClass =
    variant === "compact" ? styles.metricsGridCompact : styles.metricsGrid;

  return (
    <div className={styles.wrap}>
      <div className={gridClass}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Заряд (SoC)</span>
          <span className={styles.metricValue}>
            {summary.soc !== undefined ? `${Math.round(summary.soc)} %` : "—"}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Напруга пакета</span>
          <span className={styles.metricValue}>{fmt(summary.packVoltage, 2)} В</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Струм</span>
          <span className={styles.metricValue}>{fmt(summary.current, 2)} А</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Цикли</span>
          <span className={styles.metricValue}>
            {summary.cycles !== undefined ? String(Math.round(summary.cycles)) : "—"}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Ємність (залишок / повна)</span>
          <span className={styles.metricValue}>
            {summary.capacityLeft !== undefined || summary.capacityFull !== undefined
              ? `${fmt(summary.capacityLeft, 2)} / ${fmt(summary.capacityFull, 2)} А·г`
              : "—"}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>ΔV (комірки)</span>
          <span className={styles.metricValue}>{fmt(summary.deltaVoltage, 3)} В</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Мін / макс комірка</span>
          <span className={styles.metricValue}>
            {summary.minCellVoltage !== undefined || summary.maxCellVoltage !== undefined
              ? `${fmt(summary.minCellVoltage, 3)} … ${fmt(summary.maxCellVoltage, 3)} В`
              : "—"}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Температура</span>
          <span className={styles.metricValue}>{tempLine}</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Прошивка BMS</span>
          <span className={styles.metricValue}>
            {summary.firmware !== undefined ? String(summary.firmware) : "—"}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Дата на BMS</span>
          <span className={styles.metricValue}>{summary.bmsDate || "—"}</span>
        </div>
      </div>

      {variant === "detail" && summary.cells.length > 0 && (
        <div className={styles.cellsBlock}>
          <div className={styles.cellsTitle}>Напруги комірок</div>
          <div className={styles.cellsGrid}>
            {summary.cells.map((cell) => (
              <div key={cell.key} className={styles.cellChip}>
                <span className={styles.cellKey}>{cell.key.replace(/^cell/i, "")}</span>
                <span className={styles.cellV}>{cell.voltage.toFixed(3)} В</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button type="button" className={styles.rawButton} onClick={openRaw}>
        Сирі дані (JSON)
      </button>

      {rawOpen && normalized && (
        <DefaultModal>
          <div className={styles.modalInner} onClick={(e) => e.stopPropagation()}>
            <ModalTitle title="Сирі дані BMS" subTitle="Як отримано з BMS" onClose={() => setRawOpen(false)} />
            <pre className={styles.rawPre}>{JSON.stringify(normalized, null, 2)}</pre>
          </div>
        </DefaultModal>
      )}
    </div>
  );
};

export default BmsBatteryStatus;
